import {
	Box,
	FormLabel,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import GradientBorderTextField from '../../../../components/GradientBorderTextField/GradientBorderTextField';
import Button from '../../../../components/Button/Button';
import Sparkles from '../../../../assets/Images/Sparkles.svg';
import SparkleLight from '../../../../assets/Images/SparkleWhite.svg';
import SparkleDark from '../../../../assets/Images/Sparkle.png';
import './EmailContent.scss';
import { useEffect, useState } from 'react';
import { useAIEmailContext } from '../AIEmailContext';
import AIEmailGeneratorEndpoints from '../AIEmailGenerator.endpoints';
import useApiService from '../../../../services/api.service';
import useToast from '../../../../components/Toast/hooks/useToast';
import EditIcon from '../../../../assets/Images/EditIcon.svg';
import {
	hideLoader,
	showLoader,
} from '../../../../components/CustomLoader/CustomLoader';
import { useAppContext } from '../../../../AppContext/App.context';
import { sampleEmailData } from '../Data';
import { AppUtilityFunctions } from '../../../../utils/appUtils';

type Props = {
	emailType: number;
	segment: string;
	emailSeq: any;
	senderName: string;
	category: string;
	emailName: string;
	addingNew: boolean;
	isEmailGenerated: boolean;
};
const EmailContent = ({
	emailType,
	segment,
	senderName,
	category,
	emailSeq,
	emailName,
	addingNew,
	isEmailGenerated,
}: Props) => {
	const theme = useTheme();
	const isLightTheme = theme.palette.mode === 'light';
	const { emailData, setEmailData, setShowEmailBody } = useAIEmailContext();
	const APIService = useApiService();
	const [input, setInput] = useState('');
	const [isReadOnly, setReadOnly] = useState(true);
	const [showEditIcon, setShowEditIcon] = useState(false);
	const toaster = useToast();
	const [appData]: any = useAppContext();
	const { userData } = appData;
	const { user_id } = userData;
	const isDemoAcc = user_id === 989213 || user_id === 990001;

	useEffect(() => {
		if (emailSeq?.value) {
			setInput(emailSeq?.value);
		} else {
			setInput('');
		}
	}, [emailSeq]);

	useEffect(() => {
		if (input === '' && isEmailGenerated && addingNew) {
			setReadOnly(false);
		}
	}, [input]);

	useEffect(() => {
		emailData?.data?.email_msg?.length > 0
			? setShowEditIcon(true)
			: setShowEditIcon(false);
	}, [emailData]);

	const { setLoader } = useAIEmailContext();

	const askEmail = async () => {
		// setLoader(true);
		showLoader();
		const request = {
			reqBody: {
				input,
				email_type: emailType,
				segment,
				sender_name: senderName,
				email_criteria: category,
				objective: emailSeq?.label,
				email_name: emailName,
			},
		};

		try {
			const res = await APIService.post(
				AIEmailGeneratorEndpoints.askEmailGenerator(request),
				true
			);
			setEmailData(res);

			hideLoader();
			// setLoader(false);
		} catch (err) {
			toaster.addToast({
				message: 'Something went Wrong',
				timeout: 2000,
				type: 'error',
			});
			hideLoader();
			// setLoader(false);
		} finally {
		}
	};

	const onEditIconClick = () => {
		setReadOnly(false);
	};

	const getGeneratedEmail = async () => {
		const { data } = emailData;
		showLoader();
		const request = {
			reqBody: {
				final_state: data,
				new_instructions: input,
			},
		};
		try {
			const res = await APIService.post(
				AIEmailGeneratorEndpoints.humanFeedback(request),
				true
			);
			// setLoader(false);
			hideLoader();
			setEmailData(res);
			toaster.addToast({
				message: 'Regenerated You Email',
				timeout: 2000,
				type: 'success',
			});
		} catch (err) {
			toaster.addToast({
				message: err.message,
				timeout: 2000,
				type: 'error',
			});
			console.error(err);
		} finally {
			hideLoader();
			// setLoader(false);
		}
	};

	const checkValidations = () => {
		if (!segment) {
			toaster.addToast({
				message: 'Please select a Segment',
				timeout: 2000,
				type: 'error',
			});
			return;
		}
		if (!emailName) {
			toaster.addToast({
				message: 'Please enter the Email Name',
				timeout: 2000,
				type: 'error',
			});
			return;
		}
		if (!senderName) {
			toaster.addToast({
				message: 'Please enter the Sender Name',
				timeout: 2000,
				type: 'error',
			});
			return;
		}

		if (!input) {
			toaster.addToast({
				message: 'Please enter the Prompt',
				timeout: 2000,
				type: 'error',
			});
			return;
		}

		onGenerateEmailClick();
	};

	const onInputChange = (value) => {
		setInput(value);
	};

	const onGenerateEmailClick = () => {
		setShowEmailBody(true);
		if (!isReadOnly && showEditIcon) {
			getGeneratedEmail();

			return;
		}
		if (isDemoAcc || AppUtilityFunctions.isFreemiumUser()) {
			showLoader();
			setTimeout(() => {
				setEmailData(sampleEmailData);
				hideLoader();
			}, 2000);
			return;
		}
		askEmail();
	};

	return (
		<Box>
			<Box display='flex'>
				<img
					src={isLightTheme ? SparkleLight : SparkleDark}
					alt='sparkle'
					style={{ marginRight: '10px' }}
				/>
				<Box>
					<Typography
						fontSize={'18px'}
						fontWeight={600}
						display='flex'
						alignItems='center'
					>
						AI Email Generator
					</Typography>
					<Typography fontSize={'14px'} color='gray'>
						Describe what kind of email you want, and AI will write it for you
					</Typography>
				</Box>
			</Box>
			<Box display='flex' flexDirection='column'>
				<Box display='flex' alignItems='center'>
					<FormLabel
						id='demo-row-radio-buttons-group-label'
						style={{
							color: theme.palette.text.primary,
							marginBottom: '10px',
							marginTop: '30px',
						}}
					>
						Describe your email *
					</FormLabel>
					{showEditIcon && (
						<IconButton
							onClick={() => onEditIconClick()}
							style={{ marginTop: '20px', marginLeft: '6px' }}
						>
							<img src={EditIcon} alt='edit' height={20} width={20} />
						</IconButton>
					)}
				</Box>
				<GradientBorderTextField
					value={input}
					multiline={true}
					InputProps={{ readOnly: showEditIcon && isReadOnly }}
					onChange={(e) => {
						onInputChange(e.target.value);
					}}
					placeholder='Generate Your Prompt...'
				/>
			</Box>
			<Button
				disable={showEditIcon && isReadOnly}
				type='btn'
				btnText={`${
					!isReadOnly && showEditIcon ? 'Regenerate' : 'Generate'
				} Email with AI`}
				startIcon={<img src={Sparkles} alt='Sparkles' />}
				className='generate-btn'
				onClick={() => {
					checkValidations();
				}}
			/>

			{/* <SuggestedPrompts handlePromptClick={handlePromptClick} /> */}
		</Box>
	);
};

export default EmailContent;
